import type { EventProgramItem } from "@/types/EventProgramItem";
import type { EventProgramme } from "@/types/EventProgramme";

/**
 * Welle C — "Main" is a real track. Since the server creates "Main" as an
 * ordinary track with an event's first track (and moves the items without
 * one into it), there is no virtual Main bucket any more: every surface
 * lists the event's tracks, and only items whose track is missing or gone
 * fall into a neutral safety lane ("No track") that exists only while they
 * do. These are the pure rules all of those surfaces share.
 */

/** `seedKey` of the server-created "Main" track. */
export const MAIN_SEED_KEY = "main";

/**
 * Bucket/lane token of items without a live track (none, or a dangling id).
 * The SAME literal as the ?hiddenTracks= token, so hiding the safety lane
 * and filtering its items is one set lookup.
 */
export const ORPHAN_LANE_KEY = "unassigned";

/** effectiveTrackKey of a plenary (spansAllTracks) item. */
export const ALL_TRACKS_KEY = "all";

/** The literal name the server stores for the seeded Main. */
export const MAIN_SEED_NAME = "Main";

/**
 * The name a track SHOWS: the seeded Main is translated (t("programmeMain")
 * of the caller's namespace — contentHub and websiteBuilder.programManager
 * both carry it) as long as the server still marks it seeded AND it still
 * carries the seeded literal; a rename clears the marker, and from then on
 * the admin's literal name wins. The name check makes that robust against a
 * stale marker — a rename served by an older backend pod (no seedKey rule)
 * keeps the seed, and translating would hide the admin's new name. A user
 * track that merely happens to be called "Main" is never translated.
 */
export function programmeDisplayName(
  programme: Pick<EventProgramme, "name" | "seedKey">,
  t: (key: string) => string,
): string {
  return isSeededMain(programme) ? t("programmeMain") : programme.name;
}

/**
 * Whether a track IS the seeded Main: the server's marker AND the seeded
 * literal. The one "is Main" rule — the display name above, the JSON export
 * and the import's track mapping all ask this, so a renamed track with a
 * stale marker is an ordinary track everywhere (never shown translated,
 * never exported as Main, never mapped onto anybody's Main).
 */
export function isSeededMain(
  programme: Pick<EventProgramme, "name" | "seedKey">,
): boolean {
  return programme.seedKey === MAIN_SEED_KEY && programme.name === MAIN_SEED_NAME;
}

/** Append slot of a new track: max(sortIndex) + 1 (0 for the first). The
 *  list length is wrong as soon as a slot was skipped or a track deleted —
 *  the new track then ties an existing one. */
export function nextProgrammeSortIndex(
  programmes: readonly Pick<EventProgramme, "sortIndex">[],
): number {
  let max = -1;
  for (const programme of programmes) {
    const index = programme.sortIndex;
    if (typeof index === "number" && Number.isFinite(index) && index > max) {
      max = index;
    }
  }
  return max + 1;
}

/**
 * The bucket an item belongs to: ALL_TRACKS_KEY for a plenary item (it
 * belongs to every track and no track filter ever hides it), its track id
 * while that track is live, ORPHAN_LANE_KEY otherwise — a dangling id (the
 * track was deleted elsewhere, the cache not refetched yet) is an orphan, not
 * a phantom bucket nobody can hide.
 */
export function effectiveTrackKey(
  item: Pick<EventProgramItem, "programmeId" | "spansAllTracks">,
  knownIds: ReadonlySet<string>,
): string {
  if (item.spansAllTracks === true) return ALL_TRACKS_KEY;
  if (item.programmeId && knownIds.has(item.programmeId)) {
    return item.programmeId;
  }
  return ORPHAN_LANE_KEY;
}

/**
 * The track a NEW item lands in when nothing more specific was picked: the
 * first VISIBLE track in display order, else the first track (an event with
 * tracks never creates track-less items), undefined for an event without
 * tracks — those stay flat.
 */
export function defaultTrackId(
  programmes: readonly Pick<EventProgramme, "programmeId">[],
  hidden?: ReadonlySet<string>,
): string | undefined {
  const visible = programmes.find(
    (p) => !!p.programmeId && !hidden?.has(p.programmeId),
  );
  return (visible ?? programmes.find((p) => !!p.programmeId))?.programmeId;
}
